const ADS_ENABLED = import.meta.env.VITE_ADS_ENABLED === 'true';
const ADSENSE_CLIENT = import.meta.env.VITE_ADSENSE_CLIENT || '';
const REWARDED_ENABLED = import.meta.env.VITE_REWARDED_ENABLED === 'true';
const TEST_MODE = import.meta.env.VITE_H5_ADS_TEST_MODE === 'true';

const SCRIPT_SELECTOR = 'script[data-sudoku-adsense]';
const SCRIPT_LOAD_TIMEOUT_MS = 10_000;
const REWARDED_START_TIMEOUT_MS = 30_000;
const REWARDED_PLAYBACK_TIMEOUT_MS = 5 * 60_000;

export type H5RewardedResult = 'viewed' | 'dismissed' | 'unavailable' | 'error';

interface PlacementInfo {
  breakStatus?: string;
}

interface RewardedPlacement {
  type: 'reward';
  name: string;
  beforeReward: (showAd: () => void) => void;
  adViewed: () => void;
  adDismissed: () => void;
  adBreakDone: (placement: PlacementInfo) => void;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    adBreak?: (placement: RewardedPlacement) => void;
    adConfig?: (config: Record<string, unknown>) => void;
  }
}

let scriptPromise: Promise<boolean> | null = null;
let rewardedConfigured = false;
let rewardedInFlight: Promise<H5RewardedResult> | null = null;

function configurePlacementApi(): void {
  window.adsbygoogle = window.adsbygoogle || [];
  window.adBreak = window.adBreak || ((placement) => window.adsbygoogle?.push(placement));
  window.adConfig = window.adConfig || ((config) => window.adsbygoogle?.push(config));

}

export function initializeWebAds(): Promise<boolean> {
  if (!ADS_ENABLED || !ADSENSE_CLIENT || typeof window === 'undefined') {
    return Promise.resolve(false);
  }
  if (scriptPromise) return scriptPromise;

  configurePlacementApi();
  scriptPromise = new Promise<boolean>((resolve) => {
    let settled = false;
    const existing = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
    const finish = (loaded: boolean, script: HTMLScriptElement) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (!loaded) {
        script.remove();
        scriptPromise = null;
      }
      resolve(loaded);
    };
    const timeoutId = window.setTimeout(() => {
      const script = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
      if (script) finish(false, script);
      else {
        scriptPromise = null;
        resolve(false);
      }
    }, SCRIPT_LOAD_TIMEOUT_MS);

    if (existing) {
      if (existing.dataset.loaded === 'true') finish(true, existing);
      else {
        existing.addEventListener('load', () => finish(true, existing), { once: true });
        existing.addEventListener('error', () => finish(false, existing), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    script.dataset.sudokuAdsense = 'true';
    if (TEST_MODE && REWARDED_ENABLED) script.dataset.adbreakTest = 'on';
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      finish(true, script);
    }, { once: true });
    script.addEventListener('error', () => finish(false, script), { once: true });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function placementResult(status: string | undefined, viewed: boolean, dismissed: boolean): H5RewardedResult {
  if (viewed || status === 'viewed') return 'viewed';
  if (dismissed || status === 'dismissed') return 'dismissed';
  if (['noAdPreloaded', 'frequencyCapped', 'ignored', 'notReady', 'timeout'].includes(status || '')) {
    return 'unavailable';
  }
  return 'error';
}

export function requestRewardedAd(name: string): Promise<H5RewardedResult> {
  if (!ADS_ENABLED || !REWARDED_ENABLED || !ADSENSE_CLIENT || typeof window === 'undefined') {
    return Promise.resolve('unavailable');
  }
  if (rewardedInFlight) return rewardedInFlight;

  rewardedInFlight = (async () => {
    if (!(await prepareH5RewardedAds()) || !window.adBreak) return 'error';

    return new Promise<H5RewardedResult>((resolve) => {
      let viewed = false;
      let dismissed = false;
      let settled = false;
      let timeoutId: number;
      const finish = (result: H5RewardedResult) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve(result);
      };
      const setPlaybackTimeout = () => {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => finish('error'), REWARDED_PLAYBACK_TIMEOUT_MS);
      };
      timeoutId = window.setTimeout(() => finish('unavailable'), REWARDED_START_TIMEOUT_MS);

      try {
        window.adBreak?.({
          type: 'reward',
          name,
          beforeReward: (showAd) => {
            setPlaybackTimeout();
            showAd();
          },
          adViewed: () => { viewed = true; },
          adDismissed: () => { dismissed = true; },
          adBreakDone: (placement) => finish(placementResult(placement.breakStatus, viewed, dismissed)),
        });
      } catch {
        finish('error');
      }
    });
  })().finally(() => {
    rewardedInFlight = null;
  });

  return rewardedInFlight;
}

export async function prepareH5RewardedAds(): Promise<boolean> {
  if (!ADS_ENABLED || !REWARDED_ENABLED || !ADSENSE_CLIENT || typeof window === 'undefined') {
    return false;
  }
  configurePlacementApi();
  if (!rewardedConfigured) {
    window.adConfig?.({ preloadAdBreaks: 'on' });
    rewardedConfigured = true;
  }
  return initializeWebAds();
}

export function isH5RewardedEnabled(): boolean {
  return ADS_ENABLED && REWARDED_ENABLED && !!ADSENSE_CLIENT;
}
